package gr.tech.GRAttestation.sections.forms

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaEraser
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.components.inputs.SelectInput
import gr.tech.GRAttestation.components.inputs.StoreProductsInput
import gr.tech.GRAttestation.infrastructure.signature.SignatureCanvas
import gr.tech.GRAttestation.infrastructure.signature.rememberSignaturePadState
import gr.tech.GRAttestation.models.attestation.AttestationForm
import gr.tech.GRAttestation.models.attestation.SalesData
import gr.tech.GRAttestation.models.skus.SkuPreviewDTO
import gr.tech.GRAttestation.styles.grstyles.DarkGreenHoverButtonStyle
import gr.tech.GRAttestation.styles.grstyles.Theme
import gr.tech.GRAttestation.styles.store.*
import gr.tech.GRAttestation.util.Constants.FONT_FAMILY
import gr.tech.GRAttestation.util.TimeUtils
import gr.tech.GRAttestation.util.formatCurrency
import gr.tech.GRAttestation.util.todayString
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.dom.*

val attestationFormStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
    }
}

val formDefaultText = Modifier
            .fontFamily(FONT_FAMILY)
            .fontSize(1.cssRem)
            .lineHeight(1.25)
            .margin(topBottom = 3.px)

val formTitleStyle = CssStyle {
    base {
        formDefaultText
            .fontWeight(FontWeight.Bold)
            .fontSize(1.25.cssRem)
            .color(Colors.Black)
    }
}

val InputRowContainer = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(0.px)
    }
    Breakpoint.MD {
        Modifier
            .flexDirection(FlexDirection.Row)
            .gap(16.px)
    }
}

val ProductRowContainer = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Row)
            .alignItems(AlignItems.Center)
            .gap(1.cssRem)
            .margin(bottom = 1.cssRem)
    }
}

val ProductSumDataStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(1.cssRem)
            .borderRadius(8.px)
            .background(rgb(238, 246, 242))
            .padding(1.cssRem)
            .border(1.px, LineStyle.Solid, rgb(208, 224, 218))
            .margin(bottom = 1.5.cssRem)
    }
    cssRule("p") {
        Modifier
            .fontSize(12.px)
            .color(rgb(106, 144, 144))
            .margin(0.px)
    }
    cssRule("p span") {
        Modifier
            .fontSize(15.px)
            .fontWeight(FontWeight.Bold)
            .color(Theme.DarkGreen.rgb)
    }
}

val monthList = listOf("January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December")

@Composable
fun AttestationForm(
    skuOptions: List<SkuPreviewDTO>,
    breakpoint: Breakpoint,
    onSubmit: (AttestationForm) -> Unit
) {

    var selectedMonth by remember { mutableStateOf(TimeUtils.getMyMonth()) }
    val salesData = remember { mutableStateListOf(SalesData()) }

    var agreeCheckbox  by remember { mutableStateOf(false) }
    var signatureBase64: String by remember { mutableStateOf("") }
    val sigState = rememberSignaturePadState()

    var formError  by remember { mutableStateOf("") }

    val validForm by remember {
        derivedStateOf {
            selectedMonth.isNotBlank() && monthList.contains(selectedMonth) &&
            salesData.all { it.quantity > 0 && it.skuModel.id > 0 } &&
            agreeCheckbox &&
            !sigState.isEmpty && signatureBase64.isNotBlank()
        }
    }

    val incentiveAmount by remember {
        derivedStateOf { salesData.sumOf { (it.skuModel.unitPrice * it.quantity)} }
    }

    val productQuantity by remember {
        derivedStateOf { salesData.sumOf { it.quantity } }
    }

    Form(
        action = "",
        attrs = attestationFormStyle.toModifier()
            .toAttrs {
                onSubmit {
                    it.preventDefault()
                }
            }
    ) {

        Div(
            attrs = InputRowContainer.toModifier().toAttrs()
        ) {
            SelectInput(
                direction = FlexDirection.Column,
                inputId = "AttestationReportingMonthInput",
                labelText = "REPORTING MONTH",
                value = selectedMonth,
                options = monthList.map { it to it },
                containerModifier = Modifier.width(33.percent),
                labelModifier = PortalInputLabelStyle.toModifier(),
                selectModifier = PortalSelectStyle.toModifier(),
                onInputChange = { event ->
                    val month = event.target.value
                    selectedMonth = if (monthList.contains(month)) {
                        event.target.value
                    } else {
                        monthList[0]
                    }
                }
            )
        }

        P(attrs = PortalInputLabelStyle.toModifier().classNames("my-2").toAttrs()) {
            Text("PRODUCTS")
        }

        StoreProductsInput(
            skuOptions = skuOptions,
            salesData = salesData,
            onRowUpdate = { index, newData ->
                salesData[index] = newData
            },
            onAddProduct = {
                salesData.add(SalesData())
            },
            onRemoveProduct = { index ->
                if (index != 0) {
                    salesData.removeAt(index)
                }
            }
        )

        // Units Sold & Incentive
        if (incentiveAmount > 0 || productQuantity > 0) {
            Row(modifier = ProductSumDataStyle.toModifier()) {
                P() {
                    Text("Units Sold: ")
                    SpanText(
                        text = productQuantity.toString(),
                        modifier = Modifier.classNames("fw-bold", "ms-1")
                    )
                }

                P() {
                    Text("Incentive: ")
                    SpanText(
                        text = formatCurrency(incentiveAmount),
                        modifier = Modifier.classNames("fw-bold", "ms-1")
                    )
                }
            }
        }


        Div(attrs = AttestationAlertContainerStyle.toModifier().toAttrs()) {

            P(Modifier.fontWeight(FontWeight.Bold).toAttrs()) {
                Text("Attestation")
            }

            Ol(AttestationAlertListStyle.toModifier().toAttrs()) {
                Li() { P() { Text("The information provided in this report is accurate and complete to the best of my knowledge.") } }
                Li() { P() { Text("All sales were made to end-use customers during the active program period.") } }
                Li() { P() { Text("The customer received the full benefit of the rebate at the point of sale.") } }
                Li() {
                    P() {
                        Text("No more than ")
                        SpanText("two (2) ", Modifier.fontWeight(FontWeight.Bold))
                        Text("qualifying fixtures were sold per customer, per location, per day.")
                    }
                }
                Li() { P() { Text("Only eligible products and SKUs on the current program schedule were promoted and invoiced.") } }
                Li() { P() { Text("All products were sold at approved store locations listed in Attachment A.") } }
            }

            Label(attrs = AttestationAlertCheckboxStyle.toModifier().toAttrs()) {
                Input(
                    type = InputType.Checkbox,
                    attrs = Modifier.toAttrs {
                        onInput { agreeCheckbox = it.value }
                    }
                )

                Text("I agree to the above attestation statements")
            }
        }

        SimpleGrid(
            numColumns = numColumns(1, 2),
            modifier = Modifier
                .gap((if (breakpoint > Breakpoint.MD) 16.px else 0.px))
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().classNames("mt-2"),
                horizontalAlignment = Alignment.Start,
            ) {
                Label(
                    forId = "SignaturePad",
                    attrs = PortalInputLabelStyle.toModifier().whiteSpace(WhiteSpace.NoWrap).fontWeight(FontWeight.Bold).margin(bottom = 8.px)
                        .toAttrs()
                ) { Text("Signature") }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.Start,
                    verticalAlignment = Alignment.Top
                ) {
                    SignatureCanvas(
                        state = sigState,
                        onChange = { signature ->
                            signature?.let { signatureBase64 = it }
                        }
                    )

                    Button(
                        attrs = Modifier.classNames("btn", "btn-outline-secondary", "ms-1")
                            .onClick {
                                it.preventDefault()
                                sigState.clear()
                            }
                            .toAttrs()
                    ) {
                        FaEraser()
                    }
                }
            }
        }

        Button(
            attrs = Modifier.classNames("btn")
                .width(175.px)
                .margin(top = 16.px, leftRight = autoLength)
                .then(DarkGreenHoverButtonStyle.toModifier())
                .disabled(!validForm)
                .thenIf(
                    condition = !validForm,
                    other = Modifier.classNames("disabled")
                )
                .onClick {
                    it.preventDefault()
                    if (validForm) {
                        onSubmit(AttestationForm(
                            reportingMonth = selectedMonth,
                            salesDataList = salesData,
                            repSignature = signatureBase64,
                            repSignDate = todayString,
                            acceptedTerms = agreeCheckbox,
                            incentiveAmount = incentiveAmount,
                            quantity = productQuantity
                        ))
                    }
                }
                .toAttrs()
        ) {
            Text("Submit Sales Report")
        }
    }
}

val TableRowPreview = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(Colors.Transparent)
            .transition(Transition.all(100.ms))
    }
    hover {
        Modifier
            .backgroundColor(rgba(240, 80, 70, 0.5))
    }
}

val TableColumnHeader = CssStyle {
    base {
        Modifier
            .fontSize(0.85.cssRem)
            .textAlign(TextAlign.Center)
    }
    Breakpoint.MD {
        Modifier
            .fontSize(1.cssRem)
    }
}

val TableColumnPreview = CssStyle {
    base {
        Modifier
            .border(1.px, LineStyle.Solid, Colors.Black)
    }
}